/**
 * ENG-5452 / FE6 — the shared entry into engagement creation.
 *
 * This is the designed create flow, not a convenience. The three source-bearing
 * creation paths (a consultation, a candidate card, an expert profile) each
 * arrive with the expert already resolved, which is why the prototype's
 * `HireExpertDrawer/ExpertSection.tsx` renders the expert read-only on all
 * three and never asks the customer to pick one. It goes further and withholds
 * the expert's email on these paths, citing spec §11.1: the expert already has a
 * platform profile, so showing it here would invite off-platform contact.
 *
 * `from_profile` is the one path with an expert and no project, so the drawer
 * asks for the project instead. See `HireExpertSource.projectId`. The other two
 * carry both, so their callers pass a complete source and the drawer shows no
 * picker at all — which is why this component takes a source rather than knowing
 * about any one entry point.
 *
 * The fourth path, `customer_introduced`, is an INVITE rather than a pick: name,
 * email and how the customer knows them, for an expert who is not on the
 * platform yet. The api cannot accept that yet, so it is ENG-5699 and not here.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Button from '@/components/Button';

import { useEngagementsActiveForAccount } from '../featureFlag';
import HireExpertDrawer, { type HireExpertSource } from './HireExpertDrawer';
import { HIRE_ACTION_LABEL } from './hireCopy';

interface HireExpertButtonProps {
  /** Where the customer is hiring FROM. The server validates the combination. */
  source: HireExpertSource;
  /** Passed through to the trigger so the host controls its own layout. */
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

export default function HireExpertButton({
  source,
  fullWidth,
  style,
}: HireExpertButtonProps): JSX.Element | null {
  // The ENTITLEMENT question only, and deliberately not the authority one
  // (ENG-5727).
  //
  // This entry point has no project until the customer picks one inside the
  // drawer, and authority is per project: it resolves through the project's
  // group, so there is nothing to ask about yet. The consultation entry point
  // reads `Project.viewer_engagement_create_denial` because it arrives holding
  // a project; here that field does not exist to read.
  //
  // What remains is legitimately a client-side question — should this render —
  // answered from an api-supplied fact rather than a local rule. And the
  // authority half is not dropped, it moves one step later: the drawer renders
  // the api's coded refusal, naming what is missing, when the create is
  // attempted. So a staff member who is not a member of the account they are
  // acting in reaches an explanation rather than a silent failure, which is
  // what ENG-5727 asked for.
  //
  // The project dropdown listing projects the viewer cannot create on is a
  // separate gap, tracked with the dropdown's own defects in ENG-5729.
  //
  // Off ⇒ the button is absent and the drawer never mounts, so none of its
  // queries are issued. The gate wraps rather than lives inside, matching
  // `ProjectEngagementsSection`.
  const enabled = useEngagementsActiveForAccount();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!enabled) return null;

  return (
    <>
      {/* v1 `components/Button`, not the v2 primitive, and matching
          `AddToExpertRequestButton` prop for prop.
          
          This renders INTO a v1 host (the profile sidebar's action stack), where
          it sits directly beneath that button. CLAUDE.md's rule for a surgical
          edit to a v1 file is to match what is there rather than migrate as a
          side effect, and a lone v2 button in that stack reads as a mistake: it
          carries a different font, shadow, fill and height from every sibling.
          Contained / lightTan / small IS the stack's button. */}
      <Button
        variant="contained"
        color="lightTan"
        size="small"
        fullWidth={fullWidth}
        style={style}
        onClick={() => setOpen(true)}
      >
        {HIRE_ACTION_LABEL}
      </Button>
      {/* Mounted only while open. The drawer reads the account's minimum hours
          and the viewer's projects on mount, and neither is worth fetching for
          a button nobody has pressed. */}
      {open ? (
        <HireExpertDrawer
          open
          onClose={() => setOpen(false)}
          source={source}
          onCreated={(engagementId) => {
            setOpen(false);
            navigate(`/engagements/${encodeURIComponent(engagementId)}`);
          }}
        />
      ) : null}
    </>
  );
}
